import React, { useEffect, useMemo, useState } from "react";
import { CalendarDays, Loader2, ChevronLeft, ChevronRight, MapPin, User, Clock, Pencil, X, CheckCircle2 } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

const MONTHS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
const DAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const HOURS = Array.from({ length: 13 }, (_, i) => 8 + i); // 8h → 20h
const STATUS_CLS = { completed: "bg-emerald-100 text-emerald-700", assigned: "bg-blue-100 text-blue-700", in_progress: "bg-indigo-100 text-indigo-700", paid_todo: "bg-blue-100 text-blue-700", requested: "bg-amber-100 text-amber-700", awaiting_payment: "bg-orange-100 text-orange-700" };

const pad = (n) => String(n).padStart(2, "0");
const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const mondayOf = (d) => { const x = new Date(d); const off = (x.getDay() + 6) % 7; x.setDate(x.getDate() - off); x.setHours(0, 0, 0, 0); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

export default function ReferentCalendrier() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("month"); // month | week
  const [cursor, setCursor] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [edit, setEdit] = useState(null);
  const [edate, setEdate] = useState("");
  const [etime, setEtime] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/referents/calendar").then((r) => setEvents(r.data.events || [])).catch(() => toast.error("Chargement impossible")).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);
  const openEdit = (ev) => { setEdit(ev); setEdate(ev.date || ""); setEtime(ev.time || ""); };
  const isMovable = (ev) => ev && !["completed"].includes(ev.status);

  const moveEvent = async (ev, newDate, newTime) => {
    if (!ev || !newDate || !isMovable(ev)) return;
    const time = newTime !== undefined ? newTime : (ev.time || "");
    if (newDate === ev.date && time === (ev.time || "")) return;
    try {
      await api.post(`/referents/missions/${ev.id}/schedule`, { scheduled_at: newDate, scheduled_time: time });
      toast.success(`Rendez-vous déplacé au ${newDate}${time ? ` à ${time}` : ""} — mission actualisée`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Déplacement impossible"); }
  };
  const saveEdit = async () => {
    if (!edate) { toast.error("Choisissez une date"); return; }
    setSaving(true);
    try { await api.post(`/referents/missions/${edit.id}/schedule`, { scheduled_at: edate, scheduled_time: etime }); toast.success("Rendez-vous mis à jour — la demande de mission est actualisée"); setEdit(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); } finally { setSaving(false); }
  };

  const byDate = useMemo(() => { const map = {}; events.forEach((e) => { (map[e.date] = map[e.date] || []).push(e); }); return map; }, [events]);
  const todayStr = new Date().toISOString().slice(0, 10);
  const upcoming = useMemo(() => events.filter((e) => e.date >= todayStr).sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time)), [events, todayStr]);

  // ----- Month grid -----
  const first = new Date(cursor.y, cursor.m, 1);
  const startOffset = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  const prevM = () => setCursor((c) => (c.m === 0 ? { y: c.y - 1, m: 11 } : { y: c.y, m: c.m - 1 }));
  const nextM = () => setCursor((c) => (c.m === 11 ? { y: c.y + 1, m: 0 } : { y: c.y, m: c.m + 1 }));
  const dstr = (d) => `${cursor.y}-${pad(cursor.m + 1)}-${pad(d)}`;

  // ----- Week grid -----
  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const prevW = () => setWeekStart((w) => addDays(w, -7));
  const nextW = () => setWeekStart((w) => addDays(w, 7));
  const hourOf = (t) => (t && /^\d{1,2}:/.test(t) ? parseInt(t.split(":")[0], 10) : null);
  // events grouped by "date|hour" within visible range
  const weekCellEvents = useMemo(() => {
    const map = {};
    events.forEach((e) => { const h = hourOf(e.time); if (h === null) return; const key = `${e.date}|${h}`; (map[key] = map[key] || []).push(e); });
    return map;
  }, [events]);
  const weekUntimed = useMemo(() => {
    const iso = weekDays.map(toISO);
    const map = {};
    events.forEach((e) => { const h = hourOf(e.time); const inWeek = iso.includes(e.date); if (inWeek && (h === null || h < 8 || h > 20)) { (map[e.date] = map[e.date] || []).push(e); } });
    return map;
  }, [events, weekDays]);
  const weekLabel = `${weekStart.getDate()} ${MONTHS[weekStart.getMonth()].slice(0, 4)}. — ${addDays(weekStart, 6).getDate()} ${MONTHS[addDays(weekStart, 6).getMonth()].slice(0, 4)}. ${addDays(weekStart, 6).getFullYear()}`;

  if (loading) return <div className="flex items-center justify-center py-24 text-brand-green/50"><Loader2 className="w-7 h-7 animate-spin" /></div>;

  const evChip = (e, extra = "") => {
    const movable = isMovable(e);
    return (
      <button key={e.id} draggable={movable} onDragStart={(ev) => ev.dataTransfer.setData("text/plain", e.id)} onClick={() => movable && openEdit(e)}
        className={`w-full text-left leading-tight px-1 py-0.5 rounded truncate ${STATUS_CLS[e.status] || "bg-brand-soft text-brand-green"} ${movable ? "hover:ring-1 hover:ring-brand-greenDeep cursor-grab active:cursor-grabbing" : ""} ${extra}`}
        title={`${e.time} ${e.title} · ${e.property_name}${movable ? " (glisser pour déplacer)" : ""}`} data-testid={`cal-event-${e.id}`}>
        {e.time ? e.time + " " : ""}{e.property_name || e.title}
      </button>
    );
  };

  return (
    <div className="space-y-6" data-testid="referent-calendrier-page">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl bg-brand-lime/30 text-brand-greenDeep flex items-center justify-center"><CalendarDays className="w-5 h-5" /></div>
        <div>
          <h2 className="font-display text-2xl font-semibold text-brand-greenDeep">Calendrier des rendez-vous</h2>
          <p className="text-sm text-brand-green/55">Vos états des lieux planifiés, partagés avec les propriétaires et locataires</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-3xl border border-brand-soft p-5" data-testid="calendar-grid">
          <div className="flex items-center justify-between mb-4 gap-3">
            {view === "month" ? (
              <button onClick={prevM} className="p-2 rounded-full hover:bg-brand-softer text-brand-greenDeep" data-testid="cal-prev"><ChevronLeft className="w-5 h-5" /></button>
            ) : (
              <button onClick={prevW} className="p-2 rounded-full hover:bg-brand-softer text-brand-greenDeep" data-testid="cal-week-prev"><ChevronLeft className="w-5 h-5" /></button>
            )}
            <h3 className="font-display text-base sm:text-lg font-semibold text-brand-greenDeep text-center flex-1">{view === "month" ? `${MONTHS[cursor.m]} ${cursor.y}` : weekLabel}</h3>
            {view === "month" ? (
              <button onClick={nextM} className="p-2 rounded-full hover:bg-brand-softer text-brand-greenDeep" data-testid="cal-next"><ChevronRight className="w-5 h-5" /></button>
            ) : (
              <button onClick={nextW} className="p-2 rounded-full hover:bg-brand-softer text-brand-greenDeep" data-testid="cal-week-next"><ChevronRight className="w-5 h-5" /></button>
            )}
          </div>

          <div className="flex items-center justify-center mb-4">
            <div className="inline-flex rounded-full bg-brand-softer p-1" data-testid="calendar-view-toggle">
              <button onClick={() => setView("month")} className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${view === "month" ? "bg-brand-greenDeep text-white" : "text-brand-green/60 hover:text-brand-greenDeep"}`} data-testid="view-month-btn">Mois</button>
              <button onClick={() => setView("week")} className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${view === "week" ? "bg-brand-greenDeep text-white" : "text-brand-green/60 hover:text-brand-greenDeep"}`} data-testid="view-week-btn">Semaine</button>
            </div>
          </div>

          {view === "month" ? (
            <>
              <div className="grid grid-cols-7 gap-1 text-center text-xs font-semibold text-brand-green/50 mb-1">{DAYS.map((d) => <div key={d} className="py-1">{d}</div>)}</div>
              <div className="grid grid-cols-7 gap-1">
                {cells.map((d, i) => {
                  if (!d) return <div key={i} />;
                  const ds = dstr(d);
                  const evs = byDate[ds] || [];
                  const isToday = ds === todayStr;
                  return (
                    <div key={i} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); const id = e.dataTransfer.getData("text/plain"); const ev = events.find((x) => x.id === id); if (ev) moveEvent(ev, ds); }} className={`min-h-[68px] rounded-xl border p-1.5 ${isToday ? "border-brand-greenDeep bg-brand-lime/10" : "border-brand-soft"}`} data-testid={`cal-day-${ds}`}>
                      <span className={`text-xs font-semibold ${isToday ? "text-brand-greenDeep" : "text-brand-green/60"}`}>{d}</span>
                      <div className="space-y-0.5 mt-0.5">
                        {evs.slice(0, 3).map((e) => evChip(e, "text-[9px]"))}
                        {evs.length > 3 && <div className="text-[9px] text-brand-green/45">+{evs.length - 3}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="overflow-x-auto" data-testid="calendar-week-view">
              <div className="min-w-[640px]">
                {/* Header row */}
                <div className="grid" style={{ gridTemplateColumns: "56px repeat(7, 1fr)" }}>
                  <div />
                  {weekDays.map((d, i) => {
                    const iso = toISO(d);
                    const isToday = iso === todayStr;
                    return (
                      <div key={i} className={`text-center py-1.5 rounded-t-lg ${isToday ? "bg-brand-lime/20" : ""}`}>
                        <div className="text-xs font-semibold text-brand-green/50">{DAYS[i]}</div>
                        <div className={`text-sm font-bold ${isToday ? "text-brand-greenDeep" : "text-brand-green/70"}`}>{d.getDate()}</div>
                      </div>
                    );
                  })}
                </div>
                {/* Untimed / all-day row */}
                {weekDays.some((d) => (weekUntimed[toISO(d)] || []).length) && (
                  <div className="grid border-b border-brand-soft" style={{ gridTemplateColumns: "56px repeat(7, 1fr)" }}>
                    <div className="text-[10px] text-brand-green/40 flex items-center justify-end pr-1.5 py-1">sans h.</div>
                    {weekDays.map((d, i) => {
                      const iso = toISO(d);
                      const evs = weekUntimed[iso] || [];
                      return <div key={i} className="p-1 space-y-0.5 border-l border-brand-soft min-h-[28px]">{evs.map((e) => evChip(e, "text-[9px]"))}</div>;
                    })}
                  </div>
                )}
                {/* Hour rows */}
                {HOURS.map((h) => (
                  <div key={h} className="grid" style={{ gridTemplateColumns: "56px repeat(7, 1fr)" }}>
                    <div className="text-[11px] text-brand-green/45 text-right pr-1.5 pt-1 border-t border-brand-soft">{pad(h)}:00</div>
                    {weekDays.map((d, i) => {
                      const iso = toISO(d);
                      const key = `${iso}|${h}`;
                      const evs = weekCellEvents[key] || [];
                      const isToday = iso === todayStr;
                      return (
                        <div key={i}
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={(e) => { e.preventDefault(); const id = e.dataTransfer.getData("text/plain"); const ev = events.find((x) => x.id === id); if (ev) moveEvent(ev, iso, `${pad(h)}:00`); }}
                          className={`min-h-[38px] p-0.5 space-y-0.5 border-t border-l border-brand-soft ${isToday ? "bg-brand-lime/5" : ""}`}
                          data-testid={`cal-slot-${iso}-${pad(h)}`}>
                          {evs.map((e) => evChip(e, "text-[9px]"))}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-brand-green/40 mt-2 text-center">Glissez un rendez-vous sur une heure pour le replanifier (la mission est actualisée et les parties notifiées).</p>
            </div>
          )}
        </div>

        <div className="bg-white rounded-3xl border border-brand-soft p-5" data-testid="calendar-agenda">
          <h3 className="font-display text-lg font-semibold text-brand-greenDeep mb-3">Prochains rendez-vous</h3>
          {upcoming.length === 0 ? (
            <p className="text-sm text-brand-green/45" data-testid="agenda-empty">Aucun rendez-vous à venir. Planifiez un rendez-vous depuis « Mes missions ».</p>
          ) : (
            <div className="space-y-2">
              {upcoming.slice(0, 12).map((e) => (
                <div key={e.id} className="rounded-2xl border border-brand-soft p-3 hover:border-brand-greenDeep transition-colors" data-testid={`agenda-item-${e.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold text-brand-greenDeep">{e.title}</span>
                    <div className="flex items-center gap-1.5">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${STATUS_CLS[e.status] || "bg-brand-soft text-brand-green"}`}>{e.status_label}</span>
                      {!["completed"].includes(e.status) && <button onClick={() => openEdit(e)} className="p-1 rounded-full hover:bg-brand-softer text-brand-greenDeep" title="Modifier / déplacer" data-testid={`agenda-edit-${e.id}`}><Pencil className="w-3.5 h-3.5" /></button>}
                    </div>
                  </div>
                  <p className="text-xs text-brand-green/60 flex items-center gap-1 mt-1"><Clock className="w-3 h-3" />{e.date}{e.time ? ` · ${e.time}` : ""}</p>
                  {e.property_name && <p className="text-xs text-brand-green/60 flex items-center gap-1"><MapPin className="w-3 h-3" />{e.property_name}</p>}
                  <p className="text-xs text-brand-green/50 flex items-center gap-1"><User className="w-3 h-3" />{e.viewer === "referent" ? (e.owner_name || "Propriétaire") : (e.referent_name || "Référent")}{e.tenant ? ` · Locataire : ${e.tenant}` : ""}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {edit && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setEdit(null)}>
          <div className="bg-white rounded-3xl p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()} data-testid="calendar-edit-modal">
            <div className="flex items-center justify-between mb-1"><h3 className="font-display text-lg font-semibold text-brand-greenDeep">Modifier le rendez-vous</h3><button onClick={() => setEdit(null)}><X className="w-5 h-5 text-brand-green/50" /></button></div>
            <p className="text-sm text-brand-green/55 mb-3">{edit.title} · {edit.property_name}</p>
            <div className="space-y-3">
              <div><label className="text-xs text-brand-green/55 mb-1 block">Date</label><input type="date" value={edate} onChange={(e) => setEdate(e.target.value)} className="w-full px-3 py-2 rounded-xl border border-brand-soft outline-none focus:border-brand-lime text-sm" data-testid="calendar-edit-date" /></div>
              <div><label className="text-xs text-brand-green/55 mb-1 block">Heure</label><input type="time" value={etime} onChange={(e) => setEtime(e.target.value)} className="w-full px-3 py-2 rounded-xl border border-brand-soft outline-none focus:border-brand-lime text-sm" data-testid="calendar-edit-time" /></div>
              <button onClick={saveEdit} disabled={saving} className="w-full py-2.5 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white text-sm font-semibold disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="calendar-edit-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Enregistrer et actualiser la mission</button>
              <p className="text-[11px] text-brand-green/40 text-center">Toute modification est répercutée dans la demande de mission et notifiée aux parties.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
